import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { ProgressBar } from "@/components/ui/ProgressBar";
import { Spinner } from "@/components/ui/Spinner";
import { getLevel, LEVELS } from "@/types";

interface ResultsAgg {
  totali: number;
  completati: number;
  avgPercentuale: number | null;
  perLivello: { livello: string | null; n: number }[];
}

interface SubRow {
  id: string;
  userId: string | null;
  userEmail: string | null;
  userNome: string | null;
  userCognome: string | null;
  nomeAnonimo: string | null;
  cognomeAnonimo: string | null;
  startedAt: string;
  completedAt: string | null;
  scoreTotale: number | null;
  scoreMassimo: number | null;
  percentuale: string | number | null;
  livello: string | null;
}

export default function ResultsOverview() {
  const { id = "" } = useParams<{ id: string }>();
  const [agg, setAgg] = useState<ResultsAgg | null>(null);
  const [subs, setSubs] = useState<SubRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [esportando, setEsportando] = useState(false);
  const [erroreExport, setErroreExport] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.get<ResultsAgg>(`/admin/tests/${id}/results`),
      api.get<SubRow[]>(`/admin/tests/${id}/submissions`),
    ])
      .then(([a, s]) => { setAgg(a); setSubs(s); })
      .finally(() => setLoading(false));
  }, [id]);

  // Il file passa dall'helper `api` (che allega il bearer token) e non da un
  // link diretto: il download lo componiamo qui da un Blob.
  async function esporta() {
    setEsportando(true); setErroreExport(null);
    try {
      const r = await api.get<{ filename: string; markdown: string }>(
        `/admin/tests/${id}/export`,
      );
      const url = URL.createObjectURL(
        new Blob([r.markdown], { type: "text/markdown;charset=utf-8" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = r.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setErroreExport(e.message ?? "Errore durante l'export");
    } finally {
      setEsportando(false);
    }
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Spinner size={32} /></div>;
  if (!agg) return <div className="p-10 text-center text-red-300">Errore caricamento.</div>;

  const avg = agg.avgPercentuale != null ? Math.round(agg.avgPercentuale) : 0;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-5">
      <header className="flex justify-between items-baseline gap-4">
        <h1 className="text-2xl font-serif text-primary">Risultati</h1>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={esporta}
            disabled={esportando}
            className="font-mono text-xs text-secondary hover:text-accent
                       transition-colors disabled:opacity-50"
          >
            {esportando ? "generazione…" : "↓ esporta dettaglio (.md)"}
          </button>
          <Link to="/admin/tests" className="font-mono text-xs text-secondary hover:text-primary">← test</Link>
        </div>
      </header>

      {erroreExport && (
        <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-3 text-sm text-red-300">
          {erroreExport}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <div className="font-mono text-xs uppercase text-secondary">Totale</div>
          <div className="text-3xl font-mono text-primary">{agg.totali}</div>
        </Card>
        <Card>
          <div className="font-mono text-xs uppercase text-secondary">Completati</div>
          <div className="text-3xl font-mono text-primary">{agg.completati}</div>
        </Card>
        <Card>
          <div className="font-mono text-xs uppercase text-secondary">Media classe</div>
          <div className="text-3xl font-mono text-primary">{avg}%</div>
        </Card>
      </div>

      <Card className="space-y-3">
        <div className="font-mono text-xs uppercase tracking-wider text-secondary">
          Distribuzione livelli
        </div>
        {LEVELS.map((lv) => {
          const found = agg.perLivello.find((p) => p.livello === lv.label);
          const n = found?.n ?? 0;
          const pct = agg.completati > 0 ? Math.round((n / agg.completati) * 100) : 0;
          return (
            <div key={lv.label} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-primary">{lv.emoji} {lv.label}</span>
                <span className="text-secondary font-mono">{n} ({pct}%)</span>
              </div>
              <ProgressBar value={pct} color={lv.color} />
            </div>
          );
        })}
      </Card>

      <Card className="space-y-3">
        <div className="flex justify-between items-center">
          <div className="font-mono text-xs uppercase tracking-wider text-secondary">
            Studenti ({subs.length})
          </div>
          <Link to={`/admin/tests/${id}/risposte`} className="text-accent font-mono text-xs">
            Vedi risposte dettagliate →
          </Link>
        </div>
        <div className="space-y-1.5">
          {subs.map((s) => {
            const nome = s.userId
              ? `${s.userNome ?? ""} ${s.userCognome ?? ""}`.trim() || s.userEmail || "—"
              : `${s.nomeAnonimo ?? ""} ${s.cognomeAnonimo ?? ""}`.trim() || "Anonimo";
            const durata = s.completedAt
              ? Math.round((new Date(s.completedAt).getTime() - new Date(s.startedAt).getTime()) / 60000)
              : null;
            const stantia = !s.completedAt &&
              Date.now() - new Date(s.startedAt).getTime() > 24 * 3600 * 1000;
            const pct = s.percentuale != null ? Math.round(Number(s.percentuale)) : null;
            const lv = s.scoreMassimo != null && s.scoreTotale != null
              ? getLevel(s.scoreTotale, s.scoreMassimo)
              : null;
            return (
              <div key={s.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                <div className="flex-1 min-w-0">
                  <div className="text-primary truncate">{nome}</div>
                  <div className="text-xs text-muted font-mono">
                    {new Date(s.startedAt).toLocaleString("it-IT")}
                    {durata != null && ` · ${durata} min`}
                  </div>
                </div>
                {!s.completedAt && stantia && <Badge color="#E8613C">Incompleta</Badge>}
                {!s.completedAt && !stantia && <Badge color="#555577">In corso</Badge>}
                {s.completedAt && pct != null && (
                  <>
                    <span className="font-mono text-sm text-primary">{pct}%</span>
                    {lv && <Badge color={lv.color}>{lv.label}</Badge>}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
